// src/components/dashboard/cases/newCase/case_id/providerLabel.js
// OPTIMIZED: caseData prop se provider name leta hai, contacts sirf modal open pe fetch

import React, { useState, useEffect } from "react";
import { HiOutlineArrowsRightLeft } from "react-icons/hi2";

const API_BASE = "https://hms.backend.hiragroupcloud.com/api";

const ProviderLabel = ({ caseNumber, providerName, onProviderChange, caseData }) => {
  const [showModal, setShowModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [currentProvider, setCurrentProvider] = useState(providerName || "");

  // Sync from props
  useEffect(() => {
    const source = caseData?.provider_name || providerName || "";
    setCurrentProvider(source);
  }, [providerName, caseData?.provider_name]);

  // Fetch contacts ONLY when modal opens
  useEffect(() => {
    if (!showModal || !caseNumber) return;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        // Account ID directly from caseData (no extra API call)
        const accountId = caseData?.account_holder?.toString().trim();
        if (!accountId) throw new Error("No account");

        const contactsRes = await fetch(`${API_BASE}/contacts/${accountId}`);
        if (!contactsRes.ok) throw new Error("Failed to load contacts");

        const { data } = await contactsRes.json();
        const contactsArray = Array.isArray(data) ? data : [];
        setContacts(contactsArray);

        if (currentProvider && contactsArray.length > 0) {
          const matchedContact = contactsArray.find(
            (c) => `${c.firstName} ${c.lastName}`.trim() === currentProvider.trim()
          );
          setSelectedContact(matchedContact || null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showModal, caseNumber, caseData?.account_holder, currentProvider]);

  const formatName = () => currentProvider || "No provider assigned";

  const handleSave = async () => {
    if (!selectedContact?._id) {
      alert("Please select a contact");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/cases/${caseNumber}/provider`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider_id: selectedContact._id }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Save failed");
      }

      const result = await res.json();
      const newProviderName = result.data.provider_name;

      setCurrentProvider(newProviderName);
      onProviderChange?.(newProviderName);
      setShowModal(false);
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>
        {`
          .provider-change-button { margin-left: 8px; background: transparent; color: #004aad; border: none; outline: none; padding: 0; cursor: pointer; font-size: 13px; line-height: 1.4; vertical-align: middle; display: inline-flex; align-items: center; justify-content: center; }
          .provider-change-button svg { width: 18px; height: 18px; color: #004aad; stroke: #004aad; }
          @media (max-width: 600px) {
            .provider-change-button { margin-left: 8px; background: transparent; color: #004aad; border: none; outline: none; padding: 0; cursor: pointer; font-size: 10px; line-height: 1.4; vertical-align: middle; display: inline-flex; align-items: center; justify-content: center; }
            .provider-change-button svg { width: 14px; height: 14px; color: #004aad; stroke: #004aad; }
          }
        `}
      </style>

      <div style={{ position: "relative", top: "0", margin: "0", padding: "0", textAlign: "left" }}>
        <p style={{ fontWeight: "bold", fontSize: "18px", margin: "0 0 2px 0", padding: "0", textAlign: "left" }}>
          Provider:
          <button
            onClick={() => setShowModal(true)}
            className="provider-change-button"
            style={{ border: "none", outline: "none", background: "transparent" }}
          >
            <HiOutlineArrowsRightLeft />
          </button>
        </p>

        <div style={{
          fontSize: "14px", fontWeight: "normal", margin: "0",
          padding: "0", lineHeight: "1.2", textAlign: "left",
        }}>
          {formatName()}
        </div>

        {showModal && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
          }}>
            <div style={{
              background: "white", padding: "24px", borderRadius: "8px",
              width: "520px", maxHeight: "80vh", overflowY: "auto",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}>
              <h4 style={{ marginTop: 0 }}>Change Provider</h4>

              {loading && <p>Loading contacts…</p>}
              {error && <p style={{ color: "red" }}>{error}</p>}

              {!loading && !error && contacts.length > 0 && (
                <>
                  <label style={{ display: "block", marginBottom: "8px" }}>Select Contact</label>
                  <select
                    value={selectedContact ? `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim() : ""}
                    onChange={(e) => {
                      const chosen = contacts.find(
                        (c) => `${c.firstName} ${c.lastName}`.trim() === e.target.value
                      );
                      setSelectedContact(chosen || null);
                    }}
                    style={{
                      width: "100%", padding: "10px", borderRadius: "4px",
                      border: "1px solid #ccc", marginBottom: "20px", fontSize: "16px",
                    }}
                  >
                    <option value="">-- Select a contact --</option>
                    {contacts.map((c) => {
                      const name = `${c.firstName || ""} ${c.lastName || ""}`.trim();
                      return (
                        <option key={c._id} value={name}>
                          {name} {c.role ? `(${c.role})` : ""}
                        </option>
                      );
                    })}
                  </select>

                  {selectedContact && (
                    <div style={{
                      padding: "10px", backgroundColor: "#f8f9fa",
                      borderRadius: "4px", marginBottom: "15px",
                    }}>
                      <strong>Selected:</strong> {`${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim()}
                    </div>
                  )}
                </>
              )}

              {!loading && !error && contacts.length === 0 && <p>No contacts found.</p>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    backgroundColor: "#ddd", border: "none",
                    padding: "8px 16px", borderRadius: "4px", cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!selectedContact || saving}
                  style={{
                    backgroundColor: "#004aad", color: "white", border: "none",
                    padding: "8px 16px", borderRadius: "4px",
                    cursor: selectedContact && !saving ? "pointer" : "not-allowed",
                    opacity: selectedContact && !saving ? 1 : 0.6,
                  }}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProviderLabel;